import React from 'react';
import { Link } from 'react-router-dom';
import '../HomePage.css';
import './HealthEnvironmentalGuidePage.css';
import googlePlayImage from '../assets/images/google-play.png';
import appleStoreImage from '../assets/images/apple-store.png';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { onAppStoreClick } from '../utils/metaPixel';

const GOOGLE_PLAY_URL = process.env.REACT_APP_GOOGLE_PLAY_URL ?? 'https://play.google.com/store/apps/details?id=com.safescope.app&utm_source=na_Med';
const APP_STORE_URL = 'https://apps.apple.com/us/app/consilium-safety/id6760517718';
const PDF_PATH = '/consilium-health-environmental-flyer.pdf';

const sourceCards = [
  {
    title: 'A patchwork, not one feed',
    body: 'Outside COVID, no health condition gets one steady national feed. Official releases and news coverage run on different clocks.',
  },
  {
    title: 'A week or more behind',
    body: 'Official case counts depend on a health department release schedule, often slower than news citing the same local sources.',
  },
  {
    title: 'Coverage varies by system',
    body: 'Air Quality Index readings blend pollutants into one number, while incident-reporting systems vary in how fast they update.',
  },
];

const methodSteps = [
  {
    step: '1',
    title: 'Find the source',
    body: 'Separate the underlying fact from the headline that brought it to you.',
  },
  {
    step: '2',
    title: 'Check the time window',
    body: 'A new article may describe an older exposure window, and official datasets may lag behind current reports.',
  },
  {
    step: '3',
    title: 'Compare reach to radius',
    body: 'Ask who was actually exposed, downwind, nearby, or otherwise affected versus who merely heard about it.',
  },
  {
    step: '4',
    title: 'Look for the caveat',
    body: 'Health and environmental data often need location, timing, severity, reporting lag, and source caveats.',
  },
];

const AppDownloadButtons: React.FC<{ placement: string }> = ({ placement }) => (
  <div className="store-buttons health-env-store-buttons" aria-label="Download Consilium">
    <a
      href={GOOGLE_PLAY_URL}
      target="_blank"
      rel="noopener noreferrer"
      className="store-button google"
      aria-label="Get Consilium on Google Play"
      onClick={onAppStoreClick('android', { placement })}
    >
      <img src={googlePlayImage} alt="Get it on Google Play" className="store-icon" />
    </a>
    <a
      href={APP_STORE_URL}
      target="_blank"
      rel="noopener noreferrer"
      className="store-button apple"
      aria-label="Download Consilium on the App Store"
      onClick={onAppStoreClick('ios', { placement })}
    >
      <img src={appleStoreImage} alt="Download on the App Store" className="store-icon" />
    </a>
  </div>
);

const HealthEnvironmentalGuidePage: React.FC = () => (
  <div className="health-env-page-wrapper">
    <Header downloadHref="#download-consilium" />

    <main className="health-env-page" id="top">
      <header className="health-env-hero">
        <div className="health-env-hero-copy">
          <div className="health-env-eyebrow">Consilium Health &amp; Environmental Intelligence</div>
          <h1>How to read local health and environmental data without getting alarmed.</h1>
          <p>
            A measles headline, a chemical spill report, or an air quality warning can travel much farther than the
            actual exposure. This guide shows how to separate what happened from who is actually affected.
          </p>
          <div className="health-env-hero-actions">
            <a href="#method" className="health-env-primary-link">Use the ten-minute method</a>
            <a href={PDF_PATH} className="health-env-secondary-link" download>Download the PDF flyer</a>
          </div>
        </div>

        <aside className="health-env-scope-card" aria-label="Sample affected area card">
          <div className="health-env-scope-map" aria-hidden="true">
            <span className="health-env-map-dot" />
            <span className="health-env-map-radius" />
          </div>
          <h2>A few blocks, not the whole metro</h2>
          <p>The useful question is not how widely the story spread. It is who was exposed, nearby, or downwind.</p>
        </aside>
      </header>

      <section className="health-env-section">
        <div className="health-env-eyebrow">Why Region-Wide Alerts Often Describe Very Local Events</div>
        <h2>The headline's reach and the incident's reach are two different things.</h2>
        <p>
          A single confirmed measles case at one school can read like a regional health emergency, even when the actual
          exposure is limited to people in a specific place at a specific time. A chemical spill at one intersection can
          become breaking news across a whole metro, even when the affected area is the spill site plus a limited
          downwind radius.
        </p>

        <div className="health-env-split-card">
          <article className="health-env-headline-card">
            <span>Breaking - 3 min ago</span>
            <h3>"Chemical Spill Downtown," Residents Say</h3>
            <p>The headline gives metro-wide reach with no radius attached.</p>
          </article>
          <article className="health-env-radius-card">
            <span>Actual affected area</span>
            <div className="health-env-radius-graphic" aria-hidden="true" />
            <h3>A few blocks, not the whole metro</h3>
            <p>The reality is the site itself, plus a limited downwind area if something becomes airborne.</p>
          </article>
        </div>

        <div className="health-env-fail-grid">
          <article>
            <span className="health-env-fail-icon health-env-fail-red" aria-hidden="true" />
            <h3>Too big to ignore</h3>
            <p>A downtown spill gets coverage everywhere, with urgency added at each share.</p>
          </article>
          <article>
            <span className="health-env-fail-icon health-env-fail-blue" aria-hidden="true" />
            <h3>Too small to cover</h3>
            <p>A small-town spill may get no coverage at all, even when people nearby need to know.</p>
          </article>
        </div>
      </section>

      <section className="health-env-section">
        <div className="health-env-eyebrow">Where The Numbers Actually Come From</div>
        <h2>A patchwork of sources, running on different clocks.</h2>
        <p>
          COVID-19 was the exception: for several years, it received regular national reporting that almost no other
          health condition receives. Outside of that, state and county health departments publish on their own schedules,
          while news coverage may cite hospitals and health officials before the formal dataset catches up.
        </p>
        <div className="health-env-source-grid">
          {sourceCards.map(({ title, body }) => (
            <article key={title}>
              <span aria-hidden="true" />
              <h3>{title}</h3>
              <p>{body}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="health-env-section" id="method">
        <div className="health-env-eyebrow">A Ten-Minute Method You Can Use On Any Claim</div>
        <h2>Read the fact without being misled by the framing.</h2>
        <p>
          Health and environmental claims need one extra check: if the source is news coverage, weigh the fact and the
          framing separately. An outlet may be right about what happened while still overstating who is affected.
        </p>
        <div className="health-env-method-grid">
          {methodSteps.map(({ step, title, body }) => (
            <article key={step}>
              <span>{step}</span>
              <h3>{title}</h3>
              <p>{body}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="health-env-consilium-card">
        <div>
          <div className="health-env-eyebrow">What Consilium Tracks That The News Does Not</div>
          <h2>Reported nationally, whether or not it ever becomes a headline.</h2>
          <p>
            Small environmental incidents, including contained spills and minor releases, often clear no news threshold
            at all even though they are required to be reported to federal systems like the EPA's National Response
            Center. That is the gap this view is built to close.
          </p>
        </div>
        <aside className="health-env-incident-card">
          <div>
            <strong>[Incident type], [City/County]</strong>
            <small>Reported [date]</small>
          </div>
          <span>Tracked via EPA National Response Center</span>
          <span className="health-env-muted-badge">Not covered by local news</span>
          <p>Consilium checks federal incident systems daily. An event does not need a headline to show up here.</p>
        </aside>
      </section>

      <section className="health-env-callout">
        <p>
          The same source, time-window, and scope questions also work for public safety data. Consilium applies this
          standard across public safety, health, and environmental data, for any region in the country.
        </p>
      </section>

      <section className="health-env-app-cta" id="download-consilium">
        <div>
          <div className="health-env-eyebrow">Download Consilium</div>
          <h2>Get the fact, not the framing.</h2>
          <p>
            Consilium tracks regional health data from official sources and the news coverage that draws on them, and
            pulls environmental incident reports straight from federal systems like the EPA's National Response Center
            daily, regardless of whether local news ever picks up the story.
          </p>
        </div>
        <AppDownloadButtons placement="health-environmental-guide" />
      </section>

      <section className="health-env-download-card">
        <h2>Want the hardcopy?</h2>
        <p>Download the PDF flyer version of this guide for offline reading or sharing.</p>
        <a href={PDF_PATH} className="health-env-primary-link" download>Download PDF</a>
      </section>

      <section className="health-env-companion">
        <h2>Companion Guides</h2>
        <p>
          Read the related Consilium guides:
          <Link to="/public-safety-data"> public safety data</Link> and
          <Link to="/breaking-news-problem"> the breaking-news problem</Link>.
        </p>
      </section>
    </main>

    <Footer />
  </div>
);

export default HealthEnvironmentalGuidePage;
